import { compact } from "lodash";
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AssessmentType, CompetencyAssessmentType } from "types/graphql-schema";

import useLabel, { UseLabelType } from "@apps/use-label/use-label";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import ComboboxGeneric, {
  ComboboxGenericOption,
} from "@components/combobox/generic-combobox";
import FormFields from "@components/form/form-fields";
import Input from "@components/input/input";
import Modal, { ModalWidth } from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { assertNonNull } from "@helpers/helpers";

import {
  ProgramFormRoleBasedReviewConfig,
  ProgramFormStepDownwardReview,
  ProgramFormStepPeerReview,
} from "../types";
import { RoleExampleQuestion } from "./example-question";

const competencyAssessmentOptions = (label: UseLabelType) => [
  {
    value: CompetencyAssessmentType.CriteriaCombined,
    label: `Assess ${label("competency")} as a whole`,
  },
  {
    value: CompetencyAssessmentType.IndividualCriteria,
    label: `Assess each ${label("competency")} individually`,
  },
];

enum NextRoleAssessmentConfig {
  NO = "NO",
  ONLY_MANAGER = "ONLY_MANAGER",
  ONLY_SUBJECT = "ONLY_SUBJECT",
  BOTH = "BOTH",
}

const rateNextRoleOptions = [
  {
    value: NextRoleAssessmentConfig.NO,
    label: "No",
  },
  {
    value: NextRoleAssessmentConfig.ONLY_MANAGER,
    label: "Manager Only",
  },
  {
    value: NextRoleAssessmentConfig.ONLY_SUBJECT,
    label: "Employee Only",
  },
  {
    value: NextRoleAssessmentConfig.BOTH,
    label: "Both",
  },
];

const nextRoleAssessmentConfigToProgramSettings = (
  opt: ComboboxGenericOption<NextRoleAssessmentConfig>,
) => {
  if (opt.value === NextRoleAssessmentConfig.ONLY_MANAGER) {
    return {
      enabledSelfReviewForNextRole: false,
      enabledForNextRole: true,
    };
  }
  if (opt.value === NextRoleAssessmentConfig.ONLY_SUBJECT) {
    return {
      enabledSelfReviewForNextRole: true,
      enabledForNextRole: false,
    };
  }
  if (opt.value === NextRoleAssessmentConfig.BOTH) {
    return {
      enabledSelfReviewForNextRole: true,
      enabledForNextRole: true,
    };
  }
  return {
    enabledSelfReviewForNextRole: false,
    enabledForNextRole: false,
  };
};

const programSettingsToNextRoleAssessmentConfig = (
  programSettings: ProgramFormRoleBasedReviewConfig,
) => {
  if (
    programSettings.enabledForNextRole &&
    programSettings.enabledSelfReviewForNextRole
  ) {
    return NextRoleAssessmentConfig.BOTH;
  }
  if (programSettings.enabledForNextRole) {
    return NextRoleAssessmentConfig.ONLY_MANAGER;
  }
  if (programSettings.enabledSelfReviewForNextRole) {
    return NextRoleAssessmentConfig.ONLY_SUBJECT;
  }
  return NextRoleAssessmentConfig.NO;
};

const scaleSizeOptions = (startsAt: number) => [
  {
    value: 2,
    label: `${1 + startsAt}`,
  },
  {
    value: 3,
    label: `${2 + startsAt}`,
  },
  {
    value: 4,
    label: `${3 + startsAt}`,
  },
  {
    value: 5,
    label: `${4 + startsAt}`,
  },
];

const scaleStartsAtOptions = [
  {
    value: 0,
    label: "0",
  },
  {
    value: 1,
    label: "1",
  },
];

const RoleBasedAssessmentSettingsModal = ({
  assessmentType,
  title,
  isEnabling,
  onClose,
  onSave,
  canUpdate,
  selectedPerformanceAssessmentGroup,
  onChangeSettings,
}: {
  selectedPerformanceAssessmentGroup:
    | ProgramFormStepPeerReview
    | ProgramFormStepDownwardReview;
  canUpdate: boolean;
  // Matches the row on the Questions tab that opens these settings.
  title: string;
  // Opened from Add, so saving also switches these questions on.
  isEnabling: boolean;
  onClose: () => void;
  // Keeps the changes and switches these questions on.
  onSave: () => void;
  onChangeSettings: (
    settings: Partial<ProgramFormRoleBasedReviewConfig>,
  ) => void;
  assessmentType: AssessmentType;
}) => {
  const label = useLabel();

  const assessmentOptions = useMemo(
    () => competencyAssessmentOptions(label),
    [label],
  );

  const config = selectedPerformanceAssessmentGroup.roleBasedReviewConfig;
  // Changes apply as they are made, so cancelling puts these back.
  const [configWhenOpened] = useState(config);
  // A review that cannot be edited has nothing to put back or add, so its
  // buttons only close.
  const handleCancel = () => {
    if (canUpdate) {
      onChangeSettings(configWhenOpened);
    }
    onClose();
  };

  return createPortal(
    <Modal
      open
      width={ModalWidth.normal}
      onClose={handleCancel}
      aria-label="Role based assessment settings modal"
    >
      <div className="flex flex-col">
        <ModalTitle className="p-6 border-b" onClose={handleCancel}>
          {title}
        </ModalTitle>

        <div className="p-6 border-b">
          <div className="font-bold mb-6 text-sm">Advanced settings</div>
          <FormFields
            width={240}
            fields={compact([
              {
                title: `${label("competency", { capitalize: true })} ${label(
                  "review",
                )} configuration`,
                render: () => (
                  <ComboboxGeneric
                    options={assessmentOptions}
                    disabled={!canUpdate}
                    onChangeValue={(opt) =>
                      onChangeSettings({
                        competencyAssessmentType: opt.value,
                      })
                    }
                    value={assertNonNull(
                      assessmentOptions.find(
                        ({ value }) =>
                          value === config.competencyAssessmentType,
                      ),
                    )}
                  />
                ),
              },
              assessmentType === AssessmentType.Performance && {
                title: `Next role ${label("competency", {
                  capitalize: true,
                })} ${label("review")} configuration`,
                render: () => (
                  <ComboboxGeneric
                    options={assessmentOptions}
                    disabled={
                      !canUpdate ||
                      !(
                        config.enabledForNextRole ||
                        config.enabledSelfReviewForNextRole
                      )
                    }
                    onChangeValue={(opt) =>
                      onChangeSettings({
                        nextRoleCompetencyAssessmentType: opt.value,
                      })
                    }
                    value={assertNonNull(
                      assessmentOptions.find(
                        ({ value }) =>
                          value === config.nextRoleCompetencyAssessmentType,
                      ),
                    )}
                  />
                ),
              },
              assessmentType === AssessmentType.Performance && {
                title: "Assess employee's next role (if available)",
                render: () => (
                  <ComboboxGeneric
                    options={rateNextRoleOptions}
                    disabled={!canUpdate}
                    onChangeValue={(opt) =>
                      onChangeSettings(
                        nextRoleAssessmentConfigToProgramSettings(opt),
                      )
                    }
                    value={assertNonNull(
                      rateNextRoleOptions.find(
                        ({ value }) =>
                          value ===
                          programSettingsToNextRoleAssessmentConfig(config),
                      ),
                    )}
                  />
                ),
              },
            ])}
          />
        </div>

        <div className="p-6">
          <div className="font-bold mb-6 text-sm">Input</div>
          <FormFields
            width={240}
            fields={[
              {
                title: "Scale",
                render: () => (
                  <div className="flex gap-2 items-center">
                    <ComboboxGeneric
                      containerClassName="w-24"
                      options={scaleStartsAtOptions}
                      disabled={!canUpdate}
                      onChangeValue={(opt) =>
                        onChangeSettings({
                          assessmentScaleStartsAt: opt.value,
                        })
                      }
                      value={assertNonNull(
                        scaleStartsAtOptions.find(
                          ({ value }) =>
                            value === config.assessmentScaleStartsAt,
                        ),
                      )}
                    />
                    <TopicflowIcon
                      type="rightArrow"
                      className="text-gray-500 w-4 h-4 shrink-0"
                    />
                    <ComboboxGeneric
                      containerClassName="w-24"
                      options={scaleSizeOptions(
                        assertNonNull(config.assessmentScaleStartsAt),
                      )}
                      disabled={!canUpdate}
                      onChangeValue={(opt) =>
                        onChangeSettings({
                          assessmentScale: opt.value,
                          assessmentScaleLabels:
                            config.assessmentScaleLabels.length > opt.value
                              ? config.assessmentScaleLabels.slice(0, opt.value)
                              : config.assessmentScaleLabels.length < opt.value
                                ? config.assessmentScaleLabels.concat(
                                    Array(
                                      opt.value -
                                        config.assessmentScaleLabels.length,
                                    ).fill(""),
                                  )
                                : config.assessmentScaleLabels,
                        })
                      }
                      value={assertNonNull(
                        scaleSizeOptions(
                          assertNonNull(config.assessmentScaleStartsAt),
                        ).find(({ value }) => value === config.assessmentScale),
                      )}
                    />
                  </div>
                ),
              },
              ...assertNonNull(config.assessmentScaleLabels).map(
                (label, index) => {
                  return {
                    title: `Label ${
                      index + assertNonNull(config.assessmentScaleStartsAt)
                    }`,
                    render: () => (
                      <Input
                        disabled={!canUpdate}
                        placeholder="Label (optional)"
                        value={label ?? ""}
                        onChange={(e) => {
                          const newLabels = [
                            ...assertNonNull(config.assessmentScaleLabels),
                          ];
                          newLabels[index] = e.target.value;
                          onChangeSettings({
                            assessmentScaleLabels: newLabels,
                          });
                        }}
                      />
                    ),
                  };
                },
              ),
            ]}
          />
        </div>

        <div className="p-6 border-t">
          <RoleExampleQuestion config={config} />
        </div>

        <div className="p-6 border-t flex items-center justify-between">
          <Button
            theme={buttonTheme.text}
            text="Cancel"
            onClick={handleCancel}
          />
          <Button
            onClick={canUpdate ? onSave : onClose}
            theme={buttonTheme.primary}
            text={isEnabling ? "Save & Add" : "Save changes"}
          />
        </div>
      </div>
    </Modal>,
    document.body,
  );
};

export default RoleBasedAssessmentSettingsModal;
